import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

/**
 * The loading state for every card list in the app (spec R6.4, R6.7).
 *
 * It mirrors the real card's geometry — same grid, same header/body split, same
 * heights — so the page does not jump when the data lands. Composed from the
 * shadcn `Card` and `Skeleton` primitives rather than hand-rolled boxes, which
 * is also what keeps the pulse animation and radius in step with the theme.
 */
export interface SkeletonGridProps {
  /** How many placeholder cards to render. Defaults to 6. */
  count?: number;
  /** Extra classes for the grid container. */
  className?: string;
  /** Accessible description of what is loading. */
  label?: string;
}

export const SkeletonGrid = ({
  count = 6,
  className,
  label = 'Loading',
}: SkeletonGridProps) => (
  <div
    role="status"
    aria-busy="true"
    aria-label={label}
    className={cn(
      'grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3',
      className,
    )}
  >
    {Array.from({ length: count }, (_, index) => (
      <Card key={index} aria-hidden="true">
        <CardHeader className="gap-2">
          <Skeleton className="h-5 w-1/2" />
          <Skeleton className="h-4 w-3/4" />
        </CardHeader>
        <CardContent className="flex items-center gap-3">
          <Skeleton className="h-4 w-20" />
          <Skeleton className="h-4 w-24" />
        </CardContent>
      </Card>
    ))}
    <span className="sr-only">{label}</span>
  </div>
);
